import {ConfigProvider, StaticHostConfig} from '@aboutyou/config';
import {GrpcConfig} from '@aboutyou/data-fetcher';
import {HeadContext, HeadElementsProvider} from '@aboutyou/head-elements';
import {PageStateProvider} from '@aboutyou/history-page-state';
import {I18nProvider, StaticI18nConfig} from '@aboutyou/i18n';
import {FeatureFlagProvider, FeatureFlagSetup} from '@aboutyou/feature-flags';
import {
  RouterProvider,
  RouterProviderProps,
  useIsLastInteractionUIPushEvent,
  useCompleteLocation,
  CanGoBackProvider,
} from '@aboutyou/router';
import React, {useRef} from 'react';
import {GrpcClientHostConfigProvider} from './providers/GrpcClientHostConfigProvider';
import {TrackingProvider} from './providers/TrackingProvider';
import {StorageProvider, StorageState} from '@aboutyou/storage';

interface FeaturesProviderProps {
  headContext: HeadContext;
  i18nConfig: StaticI18nConfig;
  hostConfig: StaticHostConfig;
  routerConfig: RouterProviderProps;
  storage: StorageState;
  grpcConfig: GrpcConfig;
  featureFlagSetup: FeatureFlagSetup;
  documentReferrer: string;
}

export const FeaturesProvider: React.FC<
  React.PropsWithChildren<FeaturesProviderProps>
> = ({
  children,
  headContext,
  i18nConfig,
  hostConfig,
  routerConfig,
  grpcConfig,
  featureFlagSetup,
  storage,
  documentReferrer,
}) => {
  return (
    <RouterProvider {...routerConfig}>
      <StorageProvider {...storage}>
        <I18nProvider {...i18nConfig}>
          <ConfigProvider config={hostConfig}>
            <FeatureFlagProvider {...featureFlagSetup}>
              <TrackingProvider documentReferrer={documentReferrer}>
                <GrpcClientHostConfigProvider config={grpcConfig}>
                  <HeadElementsProvider headContext={headContext}>
                    <PageStateProviderWithKey>
                      <CanGoBackProvider>{children}</CanGoBackProvider>
                    </PageStateProviderWithKey>
                  </HeadElementsProvider>
                </GrpcClientHostConfigProvider>
              </TrackingProvider>
            </FeatureFlagProvider>
          </ConfigProvider>
        </I18nProvider>
      </StorageProvider>
    </RouterProvider>
  );
};

export const PageStateProviderWithKey: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const location = useCompleteLocation();
  const isLastInteractionUIPushEvent = useIsLastInteractionUIPushEvent();
  const pageKeyRef = useRef(location.key);

  if (!isLastInteractionUIPushEvent) {
    pageKeyRef.current = location.key;
  }

  return (
    <PageStateProvider pageKey={pageKeyRef.current}>
      {children}
    </PageStateProvider>
  );
};
